/**
 * Terms of Service Page
 * Route: /terms
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import SEO from '@/components/common/SEO';

const TermsOfService: React.FC = () => {
  const sections = [
    {
      title: '1. Acceptance of Terms',
      content: `By accessing or using MemAds ("the Platform"), you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this site. These terms apply to all visitors, users, and others who access or use the Platform.`,
    },
    {
      title: '2. Use of the Platform',
      content: `MemAds is a classifieds marketplace platform that allows registered users to post, browse, and respond to advertisements. You agree to use the Platform only for lawful purposes and in a way that does not infringe the rights of others. You must not misuse our Platform by knowingly introducing viruses, trojans, worms, or other malicious material. You must not attempt to gain unauthorized access to our Platform, the server on which our Platform is stored, or any server, computer, or database connected to our Platform.`,
    },
    {
      title: '3. Account Registration',
      content: `To post ads or contact sellers, you must create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to: (a) provide accurate and current information when registering, (b) maintain the security of your password and identification, (c) promptly update your account information as needed, and (d) be responsible for all activity conducted through your account. MemAds reserves the right to suspend or terminate your account if any information provided proves false, inaccurate, or violates these Terms.`,
    },
    {
      title: '4. Posting Ads',
      content: `When posting advertisements on MemAds, you warrant that: (a) you are the legal owner of the item being advertised or are authorised to sell it, (b) the ad content is truthful and not misleading, (c) the item does not fall under our list of prohibited items, (d) you will honour the price and terms stated in your ad. We reserve the right to remove any ad that violates our policies without prior notice. Prohibited items include, but are not limited to: illegal goods, counterfeit products, weapons, drugs, adult content, and stolen property.`,
    },
    {
      title: '5. Fees and Payments',
      content: `Basic listings on MemAds are free of charge. We may offer premium features, featured listings, or promotional placements for a fee. All fees are listed clearly on the Platform. By purchasing a paid feature, you agree to pay the stated fee. All payments are processed securely. We do not store your card details. Fees paid for premium services are generally non-refundable unless stated otherwise.`,
    },
    {
      title: '6. Intellectual Property',
      content: `The Platform and its original content, features, and functionality are and will remain the exclusive property of MemAds and its licensors. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of MemAds. Content you post on the Platform remains your property; however, you grant MemAds a non-exclusive, worldwide, royalty-free licence to use, reproduce, and distribute your content in connection with the operation of the Platform.`,
    },
    {
      title: '7. Disclaimer of Warranties',
      content: `MemAds is provided on an "as is" and "as available" basis without any warranties of any kind, either express or implied. We do not warrant that the Platform will be uninterrupted, error-free, or free of viruses. We do not verify the identity of users or the accuracy of listings. All transactions are solely between buyers and sellers; MemAds is not a party to any transaction and accepts no liability for any transaction conducted through the Platform.`,
    },
    {
      title: '8. Limitation of Liability',
      content: `To the fullest extent permitted by law, MemAds shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation loss of profits, data, use, goodwill, or other intangible losses, resulting from (a) your use of or inability to use the Platform; (b) any unauthorized access to or use of our servers; (c) any bugs, viruses, or the like transmitted through our Platform.`,
    },
    {
      title: '9. Governing Law',
      content: `These Terms shall be governed and construed in accordance with the laws of India, without regard to its conflict of law provisions. Any disputes relating to these terms will be subject to the exclusive jurisdiction of the courts of India.`,
    },
    {
      title: '10. Changes to Terms',
      content: `We reserve the right to modify or replace these Terms at any time. We will provide notice of significant changes by updating the date at the top of this page. Your continued use of the Platform after any changes constitutes your acceptance of the new Terms. We encourage you to review these Terms periodically.`,
    },
    {
      title: '11. Contact Us',
      content: `If you have any questions about these Terms of Service, please contact us at legal@memads.in or write to us at: MemAds Legal Team, India.`,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Terms of Service"
        description="Read the Terms of Service for using the MemAds platform. Acceptance of terms, user responsibilities, and guidelines."
        canonical="/terms"
      />
      {/* Page Header */}
      <div className="border-b border-border bg-secondary/30">
        <div className="mx-auto max-w-4xl px-4 py-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary text-primary-foreground">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground">Terms of Service</h1>
              <p className="text-sm text-muted-foreground">Last updated: June 2025</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
          <p className="text-muted-foreground leading-relaxed mb-8">
            Welcome to <strong className="text-foreground">MemAds</strong>. Please read these Terms of Service carefully before using our platform. These terms govern your use of MemAds's website, mobile applications, and related services.
          </p>

          <div className="space-y-8">
            {sections.map((section) => (
              <div key={section.title} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h2 className="font-heading text-lg font-semibold text-foreground mb-3">{section.title}</h2>
                <p className="text-muted-foreground leading-relaxed text-sm">{section.content}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-8 flex flex-wrap gap-3 text-sm">
          <span className="text-muted-foreground">Related:</span>
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
          <span className="text-border">·</span>
          <Link to="/cookies" className="text-primary hover:underline">Cookie Policy</Link>
          <span className="text-border">·</span>
          <a href="mailto:legal@memads.in" className="text-primary hover:underline">Contact Legal</a>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
